import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { feedbackAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { 
  ChatBubbleLeftRightIcon, 
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import FeedbackDetail from '../components/FeedbackDetail';
import Layout from '../components/Layout';

const FeedbackInbox = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, open, replied, closed
  const [recipientFilter, setRecipientFilter] = useState('all'); // all, manager, super_admin
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  
  // Check if user has access
  useEffect(() => {
    if (user && !isAdmin() && user.role !== 'manager-admin') {
      // Regular user - check if they're a manager
      const isManager = user.isManager || user.isTeamLead || user.hasSubordinates;
      if (!isManager) {
        toast.error('Access denied. Manager privileges required.');
        navigate('/user-dashboard');
      }
    }
  }, [user, isAdmin, navigate]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      if (recipientFilter !== 'all') {
        params.recipientType = recipientFilter;
      }

      const response = await feedbackAPI.getFeedback(params);
      setFeedbacks(response.data.feedbacks || []);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [filter, recipientFilter]);

  const getStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon, label: 'Open' },
      replied: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircleIcon, label: 'Replied' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircleIcon, label: 'Closed' }
    };
    const badge = badges[status] || badges.open;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleFeedbackClick = async (feedback) => {
    try {
      const response = await feedbackAPI.getFeedbackById(feedback.id);
      setSelectedFeedback(response.data.feedback);
      
      // Mark as read if not already read
      if (!feedback.isRead) {
        await feedbackAPI.markAsRead(feedback.id);
        fetchFeedbacks(); // Refresh list
      }
    } catch (error) {
      console.error('Error fetching feedback details:', error);
      toast.error('Failed to load feedback details');
    }
  };

  const handleCloseDetail = () => {
    setSelectedFeedback(null);
    // Only refresh if needed - we update state locally now
    fetchFeedbacks();
  };

  const handleFeedbackUpdate = (updatedFeedback) => {
    // Update the feedback in the local list without full refresh
    setFeedbacks(prevFeedbacks => 
      prevFeedbacks.map(fb => 
        fb.id === updatedFeedback.id ? updatedFeedback : fb
      )
    );
    
    // Also update the selected feedback if it's the same one
    if (selectedFeedback && selectedFeedback.id === updatedFeedback.id) {
      setSelectedFeedback(updatedFeedback);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Wrap in Layout if user is admin/manager-admin, otherwise show standalone
  const content = (
    <div className="p-6">
      {!isAdmin() && user?.role !== 'manager-admin' && (
        <button
          onClick={() => navigate('/manager-dashboard')}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Manager Dashboard
        </button>
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <ChatBubbleLeftRightIcon className="w-8 h-8 mr-3 text-indigo-600" />
          Feedback Inbox
        </h1>
        <p className="text-gray-600 mt-1">View and respond to feedback from your team</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center">
            <FunnelIcon className="w-5 h-5 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="replied">Replied</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-2">Recipient:</span>
            <select
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All</option>
              <option value="manager">Manager</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      {feedbacks.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">No feedback found</p>
          <p className="text-gray-500 text-sm mt-2">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-4">
          {feedbacks.map((feedback) => (
            <div
              key={feedback.id}
              onClick={() => handleFeedbackClick(feedback)}
              className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 ${
                feedback.isRead ? 'border-gray-300' : 'border-indigo-600'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="font-semibold text-gray-900">
                      {feedback.isAnonymous ? 'Anonymous' : (feedback.senderName || 'Unknown')}
                    </span>
                    {!feedback.isRead && (
                      <span className="flex h-2 w-2">
                        <span className="animate-ping absolute h-2 w-2 rounded-full bg-indigo-400 opacity-75"></span>
                        <span className="relative h-2 w-2 rounded-full bg-indigo-500"></span>
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{feedback.message}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>To: {feedback.recipientType === 'manager' ? 'Manager' : 'Super Admin'}</span>
                    <span>•</span>
                    <span>{formatDate(feedback.createdAt)}</span>
                    {feedback.replies.length > 0 && (
                      <>
                        <span>•</span>
                        <span>{feedback.replies.length} {feedback.replies.length === 1 ? 'reply' : 'replies'}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="ml-4 flex flex-col items-end space-y-2">
                  {getStatusBadge(feedback.status)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <FeedbackDetail
          feedback={selectedFeedback}
          onClose={handleCloseDetail}
          onUpdate={handleFeedbackUpdate}
        />
      )}
    </div>
  );
  
  // Wrap in Layout if admin/manager-admin, otherwise return standalone
  if (isAdmin() || user?.role === 'manager-admin') {
    return <Layout>{content}</Layout>;
  }
  
  return content;
};

export default FeedbackInbox;

